import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabaseService } from '../lib/supabase-services';

// Query keys
export const monthlyRatesKeys = {
  all: ['monthlyRates'] as const,
  byMonth: (year: number, month: number) => [...monthlyRatesKeys.all, 'month', year, month] as const,
  summary: (year: number, month: number) => [...monthlyRatesKeys.all, 'summary', year, month] as const,
  allSummaries: () => [...monthlyRatesKeys.all, 'summaries'] as const,
};

// Hook for getting monthly rate
export const useMonthlyRateQuery = (year: number, month: number, enabled = true) => {
  return useQuery({
    queryKey: monthlyRatesKeys.byMonth(year, month),
    queryFn: async () => {
      const supabaseService = getSupabaseService();
      return await supabaseService.getMonthlyRate(year, month);
    },
    enabled,
  });
};

// Hook for getting monthly summary
export const useMonthlySummaryQuery = (year: number, month: number, enabled = true) => {
  return useQuery({
    queryKey: monthlyRatesKeys.summary(year, month),
    queryFn: async () => {
      const supabaseService = getSupabaseService();
      return await supabaseService.getMonthlySummary(year, month);
    },
    enabled,
  });
};

// Hook for getting all monthly summaries
export const useAllMonthlySummariesQuery = () => {
  return useQuery({
    queryKey: monthlyRatesKeys.allSummaries(),
    queryFn: async () => {
      const supabaseService = getSupabaseService();
      return await supabaseService.getAllMonthlySummaries();
    },
    enabled: true,
  });
};

// Mutation for setting monthly rate
export const useSetMonthlyRateMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ year, month, hourlyRate }: { year: number; month: number; hourlyRate: number }) => {
      const supabaseService = getSupabaseService();
      return await supabaseService.insertOrUpdateMonthlyRate({ year, month, hourlyRate });
    },
    onSuccess: (id, { year, month, hourlyRate }) => {
      // Invalidate monthly rate and summary queries
      queryClient.invalidateQueries({ 
        queryKey: monthlyRatesKeys.byMonth(year, month) 
      });
      queryClient.invalidateQueries({ 
        queryKey: monthlyRatesKeys.summary(year, month) 
      });
      queryClient.invalidateQueries({ 
        queryKey: monthlyRatesKeys.allSummaries() 
      });
      
      // Update the cache with the new rate
      queryClient.setQueryData(
        monthlyRatesKeys.byMonth(year, month),
        {
          id,
          year,
          month,
          hourlyRate,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      );
    },
  });
};

export const useGetMonthlyRateQuery = (year: number, month: number) => {
  return useQuery({
    queryKey: monthlyRatesKeys.byMonth(year, month),
    queryFn: async () => {
      const supabaseService = getSupabaseService();
      return await supabaseService.getMonthlyRate(year, month);
    },
    enabled: true,
  });
};
